import React, { useEffect, useState, useRef } from 'react';
import { Search, Bell, Settings, ChevronDown, Download, ChevronRight, Star, Upload, ArrowLeftRight, Coins, Eye, EyeOff } from 'lucide-react';
import { totalPortfolioValue, spotBalance, spotUpnl, marginBalance, marginUsedMargin, availableMargin, futuresBalance, futuresUnrealizedPnl, earnBalance, earnRewards } from '../../utils/portfolioData';
interface HeaderProps {
  currentMarket?: string;
  currentPrice?: number;
  indexPrice?: number;
  priceChange?: number;
  priceChangePercent?: number;
  volume24h?: string;
  volume24hBTC?: string;
  accountBalance?: number;
}
export function Header({
  currentMarket = 'BTC/USD',
  currentPrice = 95441.1,
  indexPrice = 95453.7,
  priceChange = -1.94,
  priceChangePercent = -1.94,
  volume24h = '205M',
  volume24hBTC = '2.15K',
  accountBalance = 79.19
}: HeaderProps) {
  const [priceTickColor, setPriceTickColor] = useState<'success' | 'danger' | null>(null);
  const [prevPrice, setPrevPrice] = useState(currentPrice);
  const [isMarketDropdownOpen, setIsMarketDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [showBalance, setShowBalance] = useState(true);
  const [activeTab, setActiveTab] = useState('spot');
  const accountButtonRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    right: 0
  });
  // Portfolio change calculations
  const portfolioChange = -234.56; // USD
  const portfolioChangePercent = -0.47; // %
  const accounts = {
    main: {
      balance: totalPortfolioValue,
      availableMargin: availableMargin
    },
    spot: {
      balance: spotBalance,
      upnl: spotUpnl
    },
    margin: {
      balance: marginBalance,
      usedMargin: marginUsedMargin
    },
    futures: {
      balance: futuresBalance,
      buyingPower: 15000.0,
      positionsUpnl: futuresUnrealizedPnl
    },
    inverseFutures: {
      balance: 0.0,
      positionsUpnl: 0.0
    },
    earn: {
      balance: earnBalance,
      totalRewards: earnRewards
    }
  };
  useEffect(() => {
    if (currentPrice !== prevPrice) {
      setPriceTickColor(currentPrice > prevPrice ? 'success' : 'danger');
      setPrevPrice(currentPrice);
      const timer = setTimeout(() => {
        setPriceTickColor(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [currentPrice, prevPrice]);
  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  };
  const priceColor = priceTickColor === 'success' ? 'text-success' : priceTickColor === 'danger' ? 'text-danger' : 'text-text-primary';
  return <header className="flex items-center justify-between px-2 md:px-4 py-2.5 bg-background-primary border-b border-white/[0.04] relative z-50" data-id="element-1063">
      {/* Left: Market Selector + Stats */}
      <div className="flex items-center gap-3 md:gap-6 min-w-0" data-id="element-1064">
        {/* Market Search Dropdown */}
        <div className="relative flex-shrink-0" data-id="element-1065">
          <button onClick={() => {}} className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 bg-background-secondary hover:bg-background-elevated rounded-lg transition-colors cursor-default" data-id="element-1066">
            <Search size={16} className="text-text-tertiary hidden md:block" data-id="element-1067" />
            <img src="https://cryptologos.cc/logos/bitcoin-btc-logo.svg" alt="BTC" className="w-5 h-5 md:w-7 md:h-6 object-contain" data-id="element-1068" />
            <span className="font-semibold text-xs md:text-sm text-text-primary whitespace-nowrap" data-id="element-1069">
              BTC<span className="text-text-secondary" data-id="element-1070">/USD</span>
            </span>
            <span className="text-[10px] md:text-xs text-text-tertiary bg-background-elevated px-1 md:px-1.5 py-0.5 rounded" data-id="element-1071">
              10x
            </span>
            <span className="text-text-tertiary hidden lg:inline" data-id="element-1072">⌘K</span>
          </button>

          {isMarketDropdownOpen && <>
              <div className="fixed inset-0 z-40" onClick={() => setIsMarketDropdownOpen(false)} data-id="element-1073" />
              <div className="absolute left-0 top-full mt-2 w-[95vw] md:w-[800px] max-w-[800px] bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col" data-id="element-1074">
                {/* Top Tabs - Added depth with background and border */}
                <div className="flex items-center gap-1 px-2 py-2 bg-background-secondary border-b border-border-subtle" data-id="element-1075">
                  <button onClick={() => setActiveTab('favorites')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'favorites' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1076">
                    <Star size={16} className={activeTab === 'favorites' ? 'fill-current' : ''} data-id="element-1077" />
                    Favorites
                  </button>
                  <button onClick={() => setActiveTab('spot')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'spot' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1078">
                    Spot & Margin
                  </button>
                  <button onClick={() => setActiveTab('futures')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'futures' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1079">
                    Futures
                  </button>
                  <button onClick={() => setActiveTab('xstocks')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'xstocks' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1080">
                    xStocks
                    <span className="text-[10px] bg-brand-purple text-white px-1.5 py-0.5 rounded font-bold tracking-wide" data-id="element-1081">
                      NEW
                    </span>
                  </button>
                </div>

                {/* Search Bar & Filters Container */}
                <div className="bg-background-elevated" data-id="element-1082">
                  <div className="p-4 border-b border-border-subtle" data-id="element-1083">
                    <div className="flex items-center gap-3 px-4 py-3 bg-background-primary border border-border-default focus-within:border-brand-purple focus-within:ring-1 focus-within:ring-brand-purple rounded-full transition-all shadow-inner" data-id="element-1084">
                      <Search size={18} className="text-text-tertiary" data-id="element-1085" />
                      <input type="text" placeholder="Search markets" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none" data-id="element-1086" />
                      <div className="h-4 w-px bg-border-subtle mx-2" data-id="element-1087" />
                      <button className="flex items-center gap-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors" data-id="element-1088">
                        Quote: USD <ChevronDown size={12} data-id="element-1089" />
                      </button>
                    </div>
                  </div>

                  {/* Filter Tabs - Sub-navigation */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle overflow-x-auto scrollbar-hide" data-id="element-1090">
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-primary bg-background-secondary border border-border-subtle rounded-full hover:border-text-secondary transition-colors whitespace-nowrap" data-id="element-1091">
                      <span data-id="element-1092">🔥</span> Hot
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-1093">
                      <span data-id="element-1094">🚀</span> New Listings
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-1095">
                      <span data-id="element-1096">⚡</span> Margin
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-1097">
                      <span data-id="element-1098">%</span> Earn
                    </button>
                    <div className="h-4 w-px bg-border-subtle mx-1" data-id="element-1099" />
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-1100">
                      Categories <ChevronDown size={12} data-id="element-1101" />
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-1102">
                      Ecosystems <ChevronDown size={12} data-id="element-1103" />
                    </button>
                  </div>
                </div>

                {/* Market List - Aligned with HoldingsTable design */}
                <div className="max-h-[400px] overflow-y-auto bg-background-elevated" data-id="element-1104">
                  {/* Header */}
                  <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-4 px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider border-b border-border-subtle sticky top-0 bg-background-elevated/95 backdrop-blur-sm z-10" data-id="element-1105">
                    <div className="pl-2" data-id="element-1106">Market</div>
                    <div className="text-right" data-id="element-1107">Price</div>
                    <div className="text-right" data-id="element-1108">24h Change</div>
                    <div className="text-right" data-id="element-1109">Volume</div>
                    <div data-id="element-1110"></div>
                  </div>

                  {/* Market Rows */}
                  {[{
                logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg',
                name: 'BTC',
                quote: 'USD',
                leverage: '10x',
                price: '95,532.3',
                change: '-2.03',
                volume: '165M',
                currency: 'CHF',
                favorite: true
              }, {
                logo: 'https://cryptologos.cc/logos/tether-usdt-logo.svg',
                name: 'USDT',
                quote: 'USD',
                leverage: '2x',
                price: '0.99985',
                change: '-0.02',
                volume: '140M',
                currency: 'CHF',
                favorite: false
              }, {
                logo: 'https://cryptologos.cc/logos/usd-coin-usdc-logo.svg',
                name: 'USDC',
                quote: 'EUR',
                leverage: '3x',
                price: '0.8606',
                change: '0.19',
                volume: '86.9M',
                currency: 'CHF',
                favorite: false
              }, {
                logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg',
                name: 'ETH',
                quote: 'USD',
                leverage: '10x',
                price: '3,287.34',
                change: '-2.31',
                volume: '84.4M',
                currency: 'CHF',
                favorite: true
              }, {
                logo: 'https://cryptologos.cc/logos/solana-sol-logo.svg',
                name: 'SOL',
                quote: 'USD',
                leverage: '10x',
                price: '141.33',
                change: '-3.80',
                volume: '47.3M',
                currency: 'CHF',
                favorite: true
              }].map((market, idx) => <a key={idx} href="/trading" className="grid grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-4 px-6 py-4 hover:bg-background-elevated/30 transition-colors items-center group border-b border-border-subtle last:border-0" data-id="element-1111">
                      <div className="flex items-center gap-3 pl-2" data-id="element-1112">
                        <img src={market.logo} alt={market.name} className="w-8 h-8 object-contain group-hover:scale-110 transition-transform duration-200" data-id="element-1113" />
                        <div className="text-left" data-id="element-1114">
                          <div className="font-bold text-sm text-text-primary group-hover:text-brand-purple transition-colors" data-id="element-1115">
                            {market.name}
                            <span className="text-text-secondary font-normal group-hover:text-brand-purple-light/70" data-id="element-1116">
                              /{market.quote}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-0.5" data-id="element-1117">
                            <span className="text-[10px] font-medium text-text-tertiary bg-background-secondary px-1.5 py-0.5 rounded border border-border-subtle" data-id="element-1118">
                              {market.leverage}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right font-numbers text-sm font-medium text-text-primary" data-id="element-1119">
                        {market.price}{' '}
                        <span className="text-text-secondary text-xs font-normal" data-id="element-1120">
                          {market.quote}
                        </span>
                      </div>
                      <div className={`text-right font-numbers text-sm font-medium ${parseFloat(market.change) >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1121">
                        {parseFloat(market.change) >= 0 ? '+' : ''}
                        {market.change}%
                      </div>
                      <div className="text-right font-numbers text-sm text-text-secondary" data-id="element-1122">
                        {market.volume}{' '}
                        <span className="text-text-tertiary text-xs" data-id="element-1123">
                          {market.currency}
                        </span>
                      </div>
                      <button className="p-2 hover:bg-background-elevated rounded-full transition-colors" onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Toggle favorite logic here
                }} data-id="element-1124">
                        <Star size={16} className={market.favorite ? 'fill-warning text-warning' : 'text-text-tertiary hover:text-text-primary'} data-id="element-1125" />
                      </button>
                    </a>)}
                </div>
              </div>
            </>}
        </div>

        {/* Stats Bar */}
        <div className="flex items-center gap-3 md:gap-8 overflow-x-auto" data-id="element-1126">
          {/* Last Price */}
          <div className="flex-shrink-0" data-id="element-1127">
            <div className="text-[9px] md:text-[10px] text-text-tertiary uppercase tracking-wider mb-0.5" data-id="element-1128">
              LAST PRICE
            </div>
            <div className={`font-mono text-sm md:text-base font-semibold transition-colors duration-300 ${priceColor} whitespace-nowrap`} data-id="element-1129">
              {formatNumber(currentPrice)}{' '}
              <span className="text-text-secondary text-[10px] md:text-xs font-normal" data-id="element-1130">
                USD
              </span>
            </div>
          </div>

          {/* Index Price - Hidden on mobile */}
          <div className="hidden lg:block flex-shrink-0" data-id="element-1131">
            <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-0.5" data-id="element-1132">
              INDEX PRICE
            </div>
            <div className="font-mono text-base font-semibold text-text-primary whitespace-nowrap" data-id="element-1133">
              {formatNumber(indexPrice)}{' '}
              <span className="text-text-secondary text-xs font-normal" data-id="element-1134">
                USD
              </span>
            </div>
          </div>

          {/* 24h Change */}
          <div className="flex-shrink-0" data-id="element-1135">
            <div className="text-[9px] md:text-[10px] text-text-tertiary uppercase tracking-wider mb-0.5" data-id="element-1136">
              24H CHANGE
            </div>
            <div className="font-mono text-sm md:text-base font-semibold text-[#E85D75] whitespace-nowrap" data-id="element-1137">
              {priceChangePercent.toFixed(2)}%
            </div>
          </div>

          {/* 24h Volume - Hidden on small mobile */}
          <div className="hidden md:block flex-shrink-0" data-id="element-1138">
            <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-0.5" data-id="element-1139">
              24H VOLUME
            </div>
            <div className="font-mono text-base font-semibold text-text-primary whitespace-nowrap" data-id="element-1140">
              {volume24hBTC}{' '}
              <span className="text-text-secondary text-xs font-normal" data-id="element-1141">
                BTC
              </span>{' '}
              <span className="text-text-secondary text-xs font-normal hidden lg:inline" data-id="element-1142">
                {volume24h}USD
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Account & Actions */}
      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0" data-id="element-1143">
        <button className="p-1.5 md:p-2 hover:bg-background-elevated rounded transition-colors relative" data-id="element-1144">
          <Bell size={16} md:size={18} className="text-text-secondary" data-id="element-1145" />
          <span className="absolute top-0.5 md:top-1 right-0.5 md:right-1 w-1.5 md:w-2 h-1.5 md:h-2 bg-danger rounded-full" data-id="element-1146" />
        </button>

        <button className="p-1.5 md:p-2 hover:bg-background-elevated rounded transition-colors hidden md:block" data-id="element-1147">
          <Star size={18} className="text-text-secondary" data-id="element-1148" />
        </button>

        <div className="h-4 md:h-6 w-px bg-border-subtle hidden md:block" data-id="element-1149" />

        <div className="flex items-center gap-2 md:gap-3" data-id="element-1150">
          <div ref={accountButtonRef} className="relative" data-id="element-1151">
            <button onClick={() => {}} className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 hover:bg-background-elevated rounded-lg transition-colors cursor-default" data-id="element-1152">
              <div className="text-right" data-id="element-1153">
                <div className="text-[10px] md:text-xs text-text-secondary hidden md:block" data-id="element-1154">
                  Balance
                </div>
                <div className="font-mono text-xs md:text-sm font-semibold text-text-primary whitespace-nowrap" data-id="element-1155">
                  {showBalance ? totalPortfolioValue.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }) : '••••••'}{' '}
                  <span className="text-text-secondary text-[10px] md:text-xs" data-id="element-1156">
                    USD
                  </span>
                </div>
              </div>
              <ChevronDown size={14} md:size={16} className="text-text-secondary" data-id="element-1157" />
            </button>

            {isAccountDropdownOpen && <>
                <div className="fixed inset-0 z-[9998]" onClick={() => setIsAccountDropdownOpen(false)} data-id="element-1158" />
                <div className="fixed w-[480px] bg-[#0D0F14] border border-border-subtle rounded-xl shadow-2xl z-[9999] overflow-hidden" style={{
              top: dropdownPos.top,
              right: dropdownPos.right
            }} data-id="element-1159">
                  {/* Header */}
                  <div className="p-6 bg-[#1A1D26] border-b border-border-subtle" data-id="element-1160">
                    <div className="flex items-center justify-between mb-3" data-id="element-1161">
                      <div className="flex items-center gap-3" data-id="element-1162">
                        <span className="text-sm text-text-secondary" data-id="element-1163">
                          Total portfolio value
                        </span>
                        <button onClick={() => setShowBalance(!showBalance)} className="p-1 hover:bg-background-elevated rounded transition-colors" data-id="element-1164">
                          {showBalance ? <EyeOff size={18} className="text-text-secondary" data-id="element-1165" /> : <Eye size={18} className="text-text-secondary" data-id="element-1166" />}
                        </button>
                      </div>
                      <div className="font-mono text-2xl font-bold text-text-primary" data-id="element-1167">
                        {showBalance ? totalPortfolioValue.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }) : '••••••'}{' '}
                        <span className="text-text-secondary text-base font-normal" data-id="element-1168">
                          USD
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-4" data-id="element-1169">
                      <span className="text-sm text-text-secondary" data-id="element-1170">
                        Since yesterday
                      </span>
                      <div className="text-sm font-mono text-[#E85D75]" data-id="element-1171">
                        {portfolioChange.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                        <span className="text-text-secondary" data-id="element-1172">USD</span> (
                        {portfolioChangePercent.toFixed(2)}%)
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-5 gap-2" data-id="element-1173">
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-1174">
                        <Download size={20} className="text-white" data-id="element-1175" />
                        <span className="text-xs font-medium text-white" data-id="element-1176">
                          Deposit
                        </span>
                      </button>
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-1177">
                        <Upload size={20} className="text-white" data-id="element-1178" />
                        <span className="text-xs font-medium text-white" data-id="element-1179">
                          Withdraw
                        </span>
                      </button>
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-1180">
                        <ArrowLeftRight size={20} className="text-white" data-id="element-1181" />
                        <span className="text-xs font-medium text-white" data-id="element-1182">
                          Convert
                        </span>
                      </button>
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-1183">
                        <ArrowLeftRight size={20} className="text-white" data-id="element-1184" />
                        <span className="text-xs font-medium text-white" data-id="element-1185">
                          Transfer
                        </span>
                      </button>
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-1186">
                        <Coins size={20} className="text-white" data-id="element-1187" />
                        <span className="text-xs font-medium text-white" data-id="element-1188">
                          Earn
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Account Sections */}
                  <div className="max-h-[500px] overflow-y-auto" data-id="element-1189">
                    {/* Main Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-1190">
                      <div className="flex items-center justify-between mb-2" data-id="element-1191">
                        <div className="flex items-center gap-2" data-id="element-1192">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-1193">
                            Main
                          </span>
                          <ChevronDown size={14} className="text-text-secondary" data-id="element-1194" />
                        </div>
                        <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-1195">
                          {accounts.main.balance.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary text-xs" data-id="element-1196">
                            USD
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-1197">
                        <span className="text-text-secondary" data-id="element-1198">
                          Available margin
                        </span>
                        <span className="font-mono text-text-primary" data-id="element-1199">
                          {accounts.main.availableMargin.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-1200">USD</span>
                        </span>
                      </div>
                    </div>

                    {/* Spot Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-1201">
                      <div className="flex items-center justify-between mb-2" data-id="element-1202">
                        <span className="text-sm font-semibold text-text-primary" data-id="element-1203">
                          Spot
                        </span>
                        <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-1204">
                          {accounts.spot.balance.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary text-xs" data-id="element-1205">
                            USD
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-1206">
                        <span className="text-text-secondary" data-id="element-1207">
                          Balances UP&L
                        </span>
                        <span className="font-mono text-success" data-id="element-1208">
                          +
                          {accounts.spot.upnl.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-1209">USD</span>
                        </span>
                      </div>
                    </div>

                    {/* Margin Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-1210">
                      <div className="flex items-center justify-between mb-2" data-id="element-1211">
                        <div className="flex items-center gap-2" data-id="element-1212">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-1213">
                            Margin
                          </span>
                          <span className="w-2 h-2 rounded-full bg-success" data-id="element-1214" />
                        </div>
                        <div className="font-mono text-sm font-semibold text-danger" data-id="element-1215">
                          {accounts.margin.balance.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary text-xs" data-id="element-1216">
                            USD
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-1217">
                        <span className="text-text-secondary" data-id="element-1218">Used margin</span>
                        <span className="font-mono text-text-primary" data-id="element-1219">
                          {accounts.margin.usedMargin.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-1220">USD</span>
                        </span>
                      </div>
                    </div>

                    {/* Futures Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-1221">
                      <div className="flex items-center justify-between mb-2" data-id="element-1222">
                        <div className="flex items-center gap-2" data-id="element-1223">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-1224">
                            Futures
                          </span>
                          <button className="text-xs text-brand-purple-lighter hover:text-white transition-colors flex items-center gap-1" data-id="element-1225">
                            Transfer funds
                          </button>
                        </div>
                        <div className="text-right" data-id="element-1226">
                          <div className="text-xs text-text-secondary mb-1" data-id="element-1227">
                            Buying Power
                          </div>
                          <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-1228">
                            ≈
                            {accounts.futures.buyingPower.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            <span className="text-text-secondary text-xs" data-id="element-1229">
                              USD
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-2" data-id="element-1230">
                        <span className="text-text-secondary" data-id="element-1231">
                          Positions UP&L
                        </span>
                        <span className="font-mono text-text-secondary" data-id="element-1232">
                          No open positions
                        </span>
                      </div>
                    </div>

                    {/* Inverse Futures Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-1233">
                      <div className="flex items-center justify-between mb-2" data-id="element-1234">
                        <div className="flex items-center gap-2" data-id="element-1235">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-1236">
                            Inverse Futures
                          </span>
                          <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-1237">
                            {accounts.inverseFutures.balance.toFixed(2)}{' '}
                            <span className="text-text-secondary text-xs" data-id="element-1238">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs" data-id="element-1239">
                          <span className="text-text-secondary" data-id="element-1240">
                            Positions UP&L
                          </span>
                          <span className="font-mono text-text-secondary" data-id="element-1241">
                            No open positions
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Earn Account */}
                    <div className="p-4 hover:bg-background-secondary transition-colors" data-id="element-1242">
                      <div className="flex items-center justify-between mb-2" data-id="element-1243">
                        <span className="text-sm font-semibold text-text-primary" data-id="element-1244">
                          Earn
                        </span>
                        <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-1245">
                          {accounts.earn.balance.toFixed(2)}{' '}
                          <span className="text-text-secondary text-xs" data-id="element-1246">
                            USD
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-1247">
                        <span className="text-text-secondary" data-id="element-1248">
                          Total rewards
                        </span>
                        <span className="font-mono text-success" data-id="element-1249">
                          +
                          {accounts.earn.totalRewards.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-1250">USD</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>

          <button className="flex items-center gap-2 px-4 py-2 bg-brand-purple/20 backdrop-blur-sm hover:bg-brand-purple/30 rounded-xl transition-colors duration-200 text-white border border-[rgba(122,46,255,0.25)] border-t-[rgba(122,46,255,0.5)] hover:border-[rgba(122,46,255,0.35)] hover:border-t-[rgba(122,46,255,0.6)]" data-id="element-1251">
            <Download size={16} data-id="element-1252" />
            <span className="text-sm font-medium" data-id="element-1253">Deposit</span>
          </button>
        </div>

        <button className="p-2 hover:bg-background-elevated rounded transition-colors" data-id="element-1254">
          <Settings size={18} className="text-text-secondary" data-id="element-1255" />
        </button>
      </div>
    </header>;
}